"use client";
import "@/components/calendar/css/calendar.css";
import useContainerWidth from "@/hooks/use-container-width";
import { logger } from "@/utils/logger";
import clsx from "clsx";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-big-calendar";
import { ViewType } from "..";
import { SlotComponentProps } from "../availabilities";
import Calendar from "../calendar-view";
import { SlotComponent } from "../common/slot-component";
import { SlotTableLegend } from "../common/slot-table-legend";
import { TitleComp } from "./title-comp";
import { Slot, SlotContainerProps } from "./types";

// Slot Container Component
export function SlotContainer({
	onChange,
	onClick,
	selectCallback,
	unitId,
	defaultView,
	prefill,
	counterSlots,
	viewCounts,
	buyerSlots,
	TableLegend,
	Title,
	minSlots = 1,
	projectId,
	className,
	...props
}: SlotContainerProps): React.ReactElement {
	const startTime = prefill?.[0]?.start ? new Date(new Date(prefill[0].start)) : new Date();
	startTime.setHours(7, 0, 0, 0);
	const [selected, setSelected] = useState<Slot[]>([]);
	const [mounted, setMounted] = useState(false);
	const [date, setDate] = useState<Date>(startTime);
	const [viewCountsState, setViewCountsState] = useState(viewCounts);
	const { containerRef, containerWidth } = useContainerWidth();
	// Convert buyerSlots to Slot format for prefill
	const buyerSlotsAsSlots: Slot[] = (buyerSlots || []).map(slot => ({
		start: slot.startTime.toString(),
		end: slot.endTime.toString(),
		unitId,
	}));

	const allPrefill = React.useMemo(() => [...(prefill || []), ...buyerSlotsAsSlots], [prefill, buyerSlotsAsSlots]);
	const handleSelect = useCallback(
		(data: unknown) => {
			if (!data) {
				logger.error("Selection of past date is not allowed");
				return;
			}

			const dataArray = Array.isArray(data) ? data : [data];
			let slotSelections: Slot[] = [];

			dataArray.forEach((d: { start: Date; end: Date }) => {
				if (selected?.map(el => el.start).includes(d.start.toString())) {
					slotSelections.push(...selected.filter(el => el.start !== d.start.toString()));
				} else {
					slotSelections.push(
						...[...(selected || []), { start: d.start.toString(), end: d.end.toString(), unitId, projectId }],
					);
				}
			});

			slotSelections = slotSelections.filter(el => new Date(el.start) > new Date());
			slotSelections = Array.from(new Map(slotSelections.map(item => [item.start, item])).values());

			setSelected(slotSelections);
			if (selectCallback) selectCallback(slotSelections);
		},
		[unitId, selectCallback, selected, projectId],
	);

	useEffect(() => {
		if (allPrefill.length && !mounted) {
			handleSelect(allPrefill);
			setMounted(true);
		}
	}, [handleSelect, mounted, allPrefill]);
	useEffect(() => {
		if (containerWidth) {
			setViewCountsState(containerWidth < 640 ? 3 : viewCounts);
		}
	}, [containerWidth, viewCounts]);

	const minButtonValidation =
		selected?.length < minSlots
			? `Please provide at least ${minSlots} slots to increase visit confirmation chances.`
			: null;
	const { userId, loading, openId, setOpenId, setLoading, buyerId, ...restProps } = props;
	const calendarProps = {
		projectId,
		userId,
		loading,
		openId,
		setOpenId,
		setLoading,
		buyerId,
		...restProps,
	};
	const isLoading = loading || viewCountsState === undefined;
	const handleNavigate = (action: string) => {
		const viewCountsNumber = viewCountsState || 7;
		let newDate = dayjs(date);

		switch (action) {
			case Navigate.PREVIOUS:
			case "PREV":
				newDate = newDate.subtract(viewCountsNumber, "day");
				break;

			case Navigate.NEXT:
			case "NEXT":
				newDate = newDate.add(viewCountsNumber, "day");
				break;

			case Navigate.TODAY:
			case "TODAY":
				newDate = dayjs();
				break;

			default:
				logger.error("Invalid action to navigate", { action });
				return;
		}

		if (newDate.isValid()) {
			setDate(newDate.toDate());
		}
	};
	const getSlotComponentProps = React.useCallback(
		(slotValue: Date | string | undefined): SlotComponentProps => {
			if (!slotValue)
				return { selectedCounterSlots: false, selectedSlot: false, relationship: "buyer", slotValue: slotValue ?? "" };

			const slotDate = typeof slotValue === "string" ? new Date(slotValue) : slotValue;
			const slotTime = slotDate.getTime();

			return {
				selectedCounterSlots: (counterSlots || []).map(el => new Date(el.start).getTime()).includes(slotTime),
				selectedSlot: (selected || [])
					.filter((el: Slot) => new Date(el.start) > new Date())
					.map((el: Slot) => new Date(el.start).getTime())
					.includes(slotTime),
				slotValue,
				relationship: "buyer",
			};
		},
		[counterSlots, selected],
	);
	return (
		<>
			<div
				data-testid="slot-visit-container"
				className={clsx(
					"cursor-pointer border-none rounded-lg shadow-none relative block w-full h-inherit overflow-y-auto bg-white",
					// "hover:shadow-lg transition-shadow duration-200",
					"scrollbar-thin scrollbar-thumb-pink-300 scrollbar-track-transparent",
					{
						rounded: selectCallback,
						"rounded-full": !selectCallback,
						"max-h-64": !className, // Only apply max-height if no custom className provided
					},
					className, // Allow custom className to override defaults
				)}
				data-name={`unit-request-visit-${unitId}-${projectId}`}
				id="slotvisit"
				{...restProps}
				ref={containerRef}
			>
				{Title ?? null}
				<div
					className={"w-full relative flex-1 flex flex-col"}
					onTouchStart={e => e.stopPropagation()}
					data-testid={`slot-count-${selected.length}`}
				>
					<Calendar
						key={date.toISOString()}
						ContainerProps={{ className: "flex-1 flex flex-col min-h-0" }}
						CustomSlotComponent={({ className, slotValue }) => (
							<SlotComponent {...getSlotComponentProps(slotValue)} className={className} />
						)}
						defaultDate={date}
						defaultView={defaultView as ViewType}
						endTime={{ hour: 22, minutes: 0 }}
						noSelectionOfPastDate
						onNavigate={(v: Date) => {
							setDate(v);
						}}
						onSelectSlot={handleSelect}
						onSelecting={() => false}
						selected={selected.map(slot => ({
							...slot,
							start: typeof slot.start === "string" ? slot.start : slot.start.toISOString(),
							end: typeof slot.end === "string" ? slot.end : slot.end.toISOString(),
						}))}
						slotCount={2}
						slotDuration={60}
						startTime={{ hour: 7, minutes: 0 }}
						toolbarProps={{
							TitleComp: () => <TitleComp isVisible={selected?.length < minSlots} minSlots={minSlots} />,
							onNavigate: handleNavigate,
						}}
						viewCounts={isLoading ? 3 : viewCountsState}
						views={props.views}
						startWeekDay={new Date(date).getDay()}
						{...calendarProps}
					/>
				</div>
				<div className="flex flex-col items-center gap-2">
					<SlotTableLegend
						items={[
							{ title: "Seller Slots", selectedCounterSlots: true, relationship: "buyer" },
							{ title: "Your Slots", selectedSlot: true, relationship: "buyer" },
							{ title: "Matching Slots", selectedCounterSlots: true, selectedSlot: true, relationship: "buyer" },
						]}
					/>
				</div>
			</div>

			{!selectCallback && (
				<div className="flex py-3 sticky bottom-0 z-10 justify-between items-center bg-white">
					{TableLegend ? (
						<TableLegend />
					) : (
						<button
							className="px-4 py-2 text-primary-700 hover:text-primary-700 text-sm font-medium bg-white"
							onClick={() => onClick?.()}
						>
							Cancel
						</button>
					)}

					<p className="text-center w-64 text-sm font-bold md:text-base">
						{selected.length ? `${selected.length} slot${selected.length > 1 ? "s" : ""} selected` : ""}
					</p>

					<span {...(minButtonValidation && { onClick: () => logger.error("") })} title={minButtonValidation ?? ""}>
						<button
							className={clsx(
								"px-4 py-2 rounded text-sm font-medium transition-colors",
								selected?.length < minSlots
									? "bg-gray-300 text-gray-500 cursor-not-allowed"
									: "bg-primary text-white hover:bg-primary",
							)}
							data-testid="slot-visit-submit-button"
							disabled={selected?.length < minSlots}
							onClick={() => {
								onChange?.(selected);
								onClick?.();
							}}
						>
							Submit
						</button>
					</span>
				</div>
			)}
		</>
	);
}
